"use client";

import { useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2 } from "lucide-react";

import { createLumiApiClient } from "@/lib/lumi-api";
import { languageMap, LEARNING_LANGUAGES } from "@/components/LanguageMap";
import {
  PUBLIC_CATALOG,
  FEATURED_ID,
  PublicBook,
  coverUrl,
  textUrl,
  translatedUrl,
  difficultyLabel,
} from "./publicCatalog";

// Re-chunk aligned sentence pairs into ~1200-char source chunks (matching the
// backend's chunk_text target) so the reader/TTS see consistent chunk sizes.
const CHUNK_TARGET = 1200;

interface AlignedChunk {
  source_text: string;
  translated_text: string;
}

// A pre-translated bundle is [[{ english, <targetname> }, ...], ...]. Flatten
// the pages, pick the target column by language name, and group into chunks.
function chunksFromTranslated(
  pages: Array<Array<Record<string, string>>>,
  targetName: string,
): AlignedChunk[] {
  const key = targetName.toLowerCase();
  const chunks: AlignedChunk[] = [];
  let src = "";
  let tgt = "";
  for (const page of pages) {
    if (!Array.isArray(page)) continue;
    for (const pair of page) {
      const s = pair.english ?? pair.English ?? "";
      const t = pair[key] ?? "";
      if (!s.trim() || !t.trim()) continue;
      if (src && src.length + s.length > CHUNK_TARGET) {
        chunks.push({ source_text: src.trim(), translated_text: tgt.trim() });
        src = "";
        tgt = "";
      }
      src += s;
      tgt += t;
    }
  }
  if (src.trim()) {
    chunks.push({ source_text: src.trim(), translated_text: tgt.trim() });
  }
  return chunks;
}

const LANGUAGE_OPTIONS = LEARNING_LANGUAGES;
const LEARNING_CODES = new Set(LEARNING_LANGUAGES.map(([code]) => code));
const FALLBACK_COVER =
  "https://lumi-public-book-cover-art.s3.amazonaws.com/lumi_cover_1.jpeg";

function onCoverError(event: React.SyntheticEvent<HTMLImageElement>) {
  const img = event.currentTarget;
  if (img.src !== FALLBACK_COVER) img.src = FALLBACK_COVER;
}

export default function DiscoverStarters() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const api = useMemo(() => createLumiApiClient(), []);

  // Preselect the language passed from the home quick-start (?lang=).
  const requestedLang = searchParams.get("lang");
  const [targetLanguage, setTargetLanguage] = useState(
    requestedLang && LEARNING_CODES.has(requestedLang) ? requestedLang : "zh-CN",
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const featured = PUBLIC_CATALOG.find((b) => b.id === FEATURED_ID);
  const rest = PUBLIC_CATALOG.filter((b) => b.id !== FEATURED_ID);
  const langLabel =
    LEARNING_LANGUAGES.find(([code]) => code === targetLanguage)?.[1] ??
    (languageMap as Record<string, string>)[targetLanguage] ??
    targetLanguage;

  async function add(book: PublicBook) {
    setBusyId(book.id);
    setError(null);
    try {
      // Fast path: if the public library already ships a translation for this
      // title + language, import it directly and skip the Mimo translation pass
      // (instant, no cost). The bundle is keyed by 2-letter code.
      const usedBundle = await tryImportBundle(book);
      if (!usedBundle) {
        // Fallback: pull the source text and run the normal
        // import → translate → TTS pipeline in the learner's target language.
        const res = await fetch(textUrl(book));
        if (!res.ok) throw new Error(`Couldn't load “${book.title}”.`);
        const content = await res.text();
        await api.importTextBook({
          title: book.title,
          source_language: "en",
          target_language: targetLanguage,
          original_name: `${book.id}.txt`,
          content,
          cover_image_url: coverUrl(book),
        });
      }
      router.push("/library");
    } catch (err) {
      // Not signed in → send them to sign in rather than a dead-end error.
      if ((err as { status?: number })?.status === 401) {
        router.push("/sign-in");
        return;
      }
      setError(err instanceof Error ? err.message : "Could not add this book");
      setBusyId(null);
    }
  }

  // Returns true if a pre-translated bundle existed and was imported.
  async function tryImportBundle(book: PublicBook): Promise<boolean> {
    const targetName = (languageMap as Record<string, string>)[targetLanguage];
    if (!targetName) return false;
    let res: Response;
    try {
      res = await fetch(translatedUrl(book, targetLanguage));
    } catch {
      return false; // network/CORS — fall back to translation
    }
    if (!res.ok) return false; // no bundle for this language
    let pages: Array<Array<Record<string, string>>>;
    try {
      pages = await res.json();
    } catch {
      return false;
    }
    const chunks = chunksFromTranslated(pages, targetName);
    if (chunks.length === 0) return false;
    await api.importTranslatedBook({
      title: book.title,
      source_language: "en",
      target_language: targetLanguage,
      chunks,
      cover_image_url: coverUrl(book),
    });
    return true;
  }

  return (
    <div className="text-center">
      <h1 className="mb-8 text-5xl font-semibold text-app-text">
        Discover Your Next Story
      </h1>

      <div className="mb-10 flex items-center justify-center gap-2 text-sm text-app-body">
        <span>Read these in</span>
        <select
          className="h-10 rounded-md border border-app-border bg-app-surface px-3 text-sm text-app-text"
          onChange={(e) => setTargetLanguage(e.target.value)}
          value={targetLanguage}
          disabled={busyId !== null}
        >
          {LANGUAGE_OPTIONS.map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="mb-6 text-sm text-destructive">{error}</p>}

      {/* Featured */}
      {featured && (
        <button
          type="button"
          onClick={() => add(featured)}
          disabled={busyId !== null}
          className="group relative mx-auto mb-16 block w-full cursor-pointer rounded-lg border border-app-border bg-app-tinted p-4 text-left shadow-lg transition-shadow duration-300 hover:shadow-xl disabled:opacity-70 md:w-3/4"
        >
          <div className="absolute left-0 top-0 rounded-bl-lg rounded-tr-lg bg-app-accent px-4 py-2 text-app-on-accent">
            Recommended
          </div>
          <div className="mt-8 flex justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className="h-80 w-64 rounded-lg object-cover shadow-md"
              src={coverUrl(featured)}
              alt={featured.title}
              loading="lazy"
              onError={onCoverError}
            />
          </div>
          <div className="mt-4 text-center">
            <h3 className="text-xl font-bold text-app-text">
              {featured.title}
            </h3>
            <p className="text-app-body">
              Reading Level: {difficultyLabel(featured.level)}
            </p>
            <p className="mt-2 font-semibold text-app-teal">
              {busyId === featured.id ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Adding in {langLabel}…
                </span>
              ) : (
                `Add in ${langLabel}`
              )}
            </p>
          </div>
        </button>
      )}

      {/* Popular */}
      <h2 className="mb-8 text-3xl font-semibold text-app-text">
        Popular Books
      </h2>
      <div className="mt-12 flex justify-center">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((book) => (
            <button
              key={book.id}
              type="button"
              onClick={() => add(book)}
              disabled={busyId !== null}
              className="flex h-full cursor-pointer flex-col overflow-hidden rounded text-left shadow-lg transition-shadow duration-300 hover:shadow-xl disabled:opacity-70"
            >
              <div className="relative flex-none">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="h-64 w-full object-cover"
                  src={coverUrl(book)}
                  alt={book.title}
                  loading="lazy"
                  onError={onCoverError}
                />
                {busyId === book.id && (
                  <div className="absolute inset-0 flex items-center justify-center bg-app-text text-app-on-accent">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex flex-grow flex-col justify-end bg-app-tinted p-4">
                <h3 className="text-sm font-semibold text-app-text">
                  {book.title}
                </h3>
                <p className="text-xs text-app-body">
                  Reading Level: {difficultyLabel(book.level)}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
